import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Briefcase, MapPin, Mail, Building2, GraduationCap, Trash2, ExternalLink, Rocket, Star } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const JOB_TYPES = ["full-time", "part-time", "locum", "internship", "residency"];

function timeAgo(iso) { try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ""; } }

export default function Jobs() {
  const { user, meta } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState({ specialty: "All", employment_type: "All", degree: "All" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", hospital: "", location: "",
    employment_type: "full-time", degree_required: "", specialty: "",
    salary_range: "", contact_email: "", apply_url: "",
  });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (filter.specialty !== "All") params.specialty = filter.specialty;
      if (filter.employment_type !== "All") params.employment_type = filter.employment_type;
      if (filter.degree !== "All") params.degree = filter.degree;
      const { data } = await api.get("/jobs", { params });
      setJobs(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.hospital || !form.location || !form.contact_email) {
      toast.error("Please fill all required fields"); return;
    }
    setLoading(true);
    try {
      await api.post("/jobs", {
        ...form,
        specialty: form.specialty || null,
        degree_required: form.degree_required || null,
        salary_range: form.salary_range || null,
        apply_url: form.apply_url || null,
      });
      toast.success("Job posted");
      setOpen(false);
      setForm({ title: "", description: "", hospital: "", location: "", employment_type: "full-time",
                degree_required: "", specialty: "", salary_range: "", contact_email: "", apply_url: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this job posting?")) return;
    try { await api.delete(`/jobs/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const boost = async (id) => {
    try {
      const { data } = await api.post(`/jobs/${id}/boost/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Boost failed");
    }
  };

  // Handle Stripe checkout return
  useEffect(() => {
    const boostResult = searchParams.get("boost");
    const sessionId = searchParams.get("session_id");
    if (!boostResult) return;
    if (boostResult === "cancel") {
      toast.info("Boost canceled");
      setSearchParams({});
      return;
    }
    if (boostResult === "success" && sessionId) {
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            toast.success("Job boosted for 30 days");
            setSearchParams({});
            load();
            return;
          }
          if (data.payment_status === "expired" || data.payment_status === "failed") {
            toast.error(`Payment ${data.payment_status}`);
            setSearchParams({});
            return;
          }
        } catch {}
        if (tries < 15) setTimeout(poll, 2000);
        else { toast.info("Still processing — refresh in a moment"); setSearchParams({}); }
      };
      poll();
    }
    // eslint-disable-next-line
  }, [searchParams]);

  return (
    <div data-testid="jobs-page" className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Jobs</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[color:var(--ayur-emerald)] mt-2">Ayurveda job board</h1>
          <p className="text-[color:var(--ayur-ink-2)] mt-2 max-w-2xl">
            BAMS / MD / MS openings posted by hospitals, clinics and colleges across India — straight from the community, no recruiters.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-job-button"
              className="bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white rounded-full px-6">
              Post a job
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[color:var(--ayur-cream)] max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl">Post a job opening</DialogTitle>
              <DialogDescription className="text-[color:var(--ayur-mute)]">
                Share a role at your hospital, clinic, or college. Community moderated.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Role title *</Label>
                <Input data-testid="job-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white" placeholder="Junior Panchakarma Consultant" />
              </div>
              <div><Label>Hospital / Clinic *</Label>
                <Input data-testid="job-hospital-input" value={form.hospital} onChange={(e) => setForm({ ...form, hospital: e.target.value })} className="bg-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Location *</Label>
                  <Input data-testid="job-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-white" placeholder="Kochi, Kerala" />
                </div>
                <div><Label>Type</Label>
                  <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                    <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{JOB_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Degree required</Label>
                  <Select value={form.degree_required} onValueChange={(v) => setForm({ ...form, degree_required: v })}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>{meta.degrees.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Specialty</Label>
                  <Select value={form.specialty} onValueChange={(v) => setForm({ ...form, specialty: v })}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>{meta.specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Salary range</Label>
                <Input value={form.salary_range} onChange={(e) => setForm({ ...form, salary_range: e.target.value })} className="bg-white" placeholder="₹40-60k / month" />
              </div>
              <div><Label>Description *</Label>
                <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white resize-none" placeholder="Role, responsibilities, must-haves…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Contact email *</Label>
                  <Input data-testid="job-contact-email-input" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="bg-white" />
                </div>
                <div><Label>Apply URL</Label>
                  <Input value={form.apply_url} onChange={(e) => setForm({ ...form, apply_url: e.target.value })} className="bg-white" placeholder="https://…" />
                </div>
              </div>
              <Button type="submit" data-testid="job-submit-button" disabled={loading}
                className="w-full bg-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-emerald-2)] text-white rounded-full py-6">
                {loading ? "Posting…" : "Post job"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="paper-card p-4 flex flex-wrap gap-3">
        <Select value={filter.specialty} onValueChange={(v) => setFilter({ ...filter, specialty: v })}>
          <SelectTrigger className="bg-white w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All specialties</SelectItem>
            {meta.specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.degree} onValueChange={(v) => setFilter({ ...filter, degree: v })}>
          <SelectTrigger className="bg-white w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">Any degree</SelectItem>
            {meta.degrees.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.employment_type} onValueChange={(v) => setFilter({ ...filter, employment_type: v })}>
          <SelectTrigger className="bg-white w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">Any type</SelectItem>
            {JOB_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4">
        {jobs.length === 0 && (
          <div className="paper-card p-12 text-center">
            <div className="label-eyebrow">Nothing open right now</div>
            <h3 className="font-serif-display text-2xl mt-2 text-[color:var(--ayur-emerald)]">Be the first to post</h3>
          </div>
        )}
        {jobs.map((j) => (
          <div key={j.id} data-testid="job-card"
            className={`paper-card p-6 ${j.is_sponsored ? "border-[color:var(--ayur-gold)] bg-[color:var(--ayur-badge)]/30" : ""}`}
            style={j.is_sponsored ? { borderWidth: 2 } : {}}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {j.is_sponsored && (
                    <span className="pill inline-flex items-center gap-1" data-testid="sponsored-badge"
                      style={{ background: "rgba(212,163,89,0.2)", borderColor: "var(--ayur-gold)", color: "#8a6a2c" }}>
                      <Star className="w-3 h-3 fill-current" /> Sponsored
                    </span>
                  )}
                  <span className="pill">{j.employment_type}</span>
                  {j.specialty && <span className="pill">{j.specialty}</span>}
                  <span className="text-xs text-[color:var(--ayur-mute)]">· {timeAgo(j.created_at)}</span>
                </div>
                <h3 className="font-serif-display text-xl mt-2 text-[color:var(--ayur-ink)]">{j.title}</h3>
                <div className="text-sm text-[color:var(--ayur-ink-2)] mt-1 flex flex-wrap gap-x-4 gap-y-1">
                  <span className="flex items-center gap-1"><Building2 className="w-4 h-4 text-[color:var(--ayur-mute)]" /> {j.hospital}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-[color:var(--ayur-mute)]" /> {j.location}</span>
                  {j.degree_required && <span className="flex items-center gap-1"><GraduationCap className="w-4 h-4 text-[color:var(--ayur-mute)]" /> {j.degree_required}</span>}
                  {j.salary_range && <span className="flex items-center gap-1"><Briefcase className="w-4 h-4 text-[color:var(--ayur-mute)]" /> {j.salary_range}</span>}
                </div>
                <p className="mt-3 text-sm text-[color:var(--ayur-ink-2)] whitespace-pre-wrap">{j.description}</p>
              </div>
              {j.poster_id === user?.id && (
                <div className="flex gap-1">
                  {!j.is_sponsored && (
                    <Button data-testid="job-boost-button" size="sm" onClick={() => boost(j.id)}
                      className="bg-[color:var(--ayur-gold)] hover:bg-[color:var(--ayur-gold)]/90 text-white">
                      <Rocket className="w-3.5 h-3.5 mr-1" /> Boost
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => remove(j.id)} className="text-[color:var(--ayur-terracotta)]" data-testid="job-delete-button">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            <div className="mt-4 pt-3 border-t border-[color:var(--ayur-border)] flex flex-wrap gap-3">
              <a href={`mailto:${j.contact_email}?subject=Application: ${encodeURIComponent(j.title)}`}
                data-testid="job-apply-button"
                className="inline-flex items-center gap-2 text-sm bg-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-emerald-2)] text-white px-4 py-2 rounded-full">
                <Mail className="w-4 h-4" /> Apply via email
              </a>
              {j.apply_url && (
                <a href={j.apply_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-[color:var(--ayur-emerald)] hover:underline">
                  <ExternalLink className="w-4 h-4" /> External link
                </a>
              )}
              <div className="flex-1" />
              <span className="text-xs text-[color:var(--ayur-mute)]">Posted by {j.poster?.full_name}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
